/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import { getUserLanguageCode } from 'js/lib/language';
import { languageTagToLocalName } from 'js/lib/languageLocalized';
import { useRetracked } from 'js/lib/retracked';
import { useLocation } from 'js/lib/useRouter';

import { Button, IconButton, Popover, Typography2, useTheme } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon, GlobeIcon, TranslationIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import TranslationDropdownMenu from 'bundles/page-header/components/translation/TranslationDropdownMenu';
import TranslationDropdownMenuWithGrouping from 'bundles/page-header/components/translation/TranslationDropdownMenuWithGrouping';
import TranslationFeedbackDialog from 'bundles/page-header/components/translation/TranslationFeedbackDialog';
import { useCourseLanguageQuery } from 'bundles/page-header/components/translation/useCourseLanguageQuery';
import { usePageHeader } from 'bundles/page-header/contexts/PageHeaderContext';

import _t from 'i18n!nls/page';

import type { TranslationFeedback_TrackingInfo as TranslationFeedbackTrackingInfo } from '../../../../__generated__/graphql-types';

const COURSE_SLUG_REGEX = /\/learn\/([^/?]*)/;
const ITEM_ID_REGEX = /\/learn\/[^/]*\/[^/]*\/([^/?]*)/;
const PROGRAM_SLUG = /\/programs\/([^/?]*)/;
const SKILLSET_SLUG = /\/skillsets\/([^/?]*)/;
const SKILL_ID = /\/skills\/([^/?]*)/;
const useTrackingInfo = (courseId?: string, enterpriseOrgId?: string): TranslationFeedbackTrackingInfo | undefined => {
  const location = useLocation();

  if (courseId) {
    const courseSlug = location.pathname.match(COURSE_SLUG_REGEX)?.[1] ?? '';
    const itemId = location.pathname.match(ITEM_ID_REGEX)?.[1];
    return {
      inCourseTrackingInfo: {
        courseId,
        courseSlug,
        itemId,
      },
    };
  } else if (enterpriseOrgId) {
    const programSlug = location.pathname.match(PROGRAM_SLUG)?.[1];
    const skillSet = location.pathname.match(SKILLSET_SLUG)?.[1];
    const skills = location.pathname.match(SKILL_ID)?.[1];
    return {
      enterpriseTrackingInfo: {
        enterpriseOrgId,
        programSlug,
        skillSet,
        skills,
      },
    };
  }
};

const useStyles = () => {
  const { spacing } = useTheme();
  return {
    container: css`
      overflow: hidden;
    `,
    entry: css`
      &::marker {
        content: '';
      }
    `,
    preUnifiedButton: css`
      padding: var(--cds-spacing-150) var(--cds-spacing-100);
      width: 100%;
      transition: background-color 0.3s ease;

      :hover {
        background-color: var(--cds-color-interactive-background-primary-hover-weak);
        text-decoration: none;
      }
    `,
    icon: css`
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    `,
    chevronIcon: css`
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    `,
    button: css`
      padding: ${spacing(0, 8)};
      height: 65px;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: ${spacing(8)};
    `,
    text: css`
      display: flex;
      gap: var(--cds-spacing-50);
      align-items: center;
      width: 100%;
    `,
    buttonLabel: css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: var(--cds-color-neutral-primary-weak);
    `,
    feedbackButton: css`
      font-weight: normal;
      padding-top: ${spacing(8)};
      padding-bottom: ${spacing(8)};
    `,
  };
};

type Props = {
  courseId?: string;
  enterpriseOrgId?: string;
  wrapperElement?: 'li' | 'div';
  useIconButton?: boolean;
  ariaLabelPrefix?: string;
};
const TranslationDropdown = ({
  courseId,
  enterpriseOrgId,
  wrapperElement = 'li',
  useIconButton = false,
  ariaLabelPrefix: ariaLabelPrefixFromProps,
}: Props) => {
  const trackingInfo = useTrackingInfo(courseId, enterpriseOrgId);
  const { loading, languages, courseLanguages } = useCourseLanguageQuery(courseId);
  const retracked = useRetracked();
  const track = useTracker();

  const styles = useStyles();
  const [dropdownButton, setDropdownButton] = useState<HTMLButtonElement | null>(null);
  const isPopoverOpen = Boolean(dropdownButton);

  const [feedbackDialogOpened, setFeedbackDialogOpened] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isSimplifiedPageHeader } = usePageHeader();
  const showTranslationDropdownWithGrouping = !!courseId;

  const locale = getUserLanguageCode();
  if (loading || !languages || !locale) return null;

  const Element = wrapperElement;

  const ariaLabelPrefix = ariaLabelPrefixFromProps ?? _t('Select language:');
  const ariaLabel = languageTagToLocalName(locale)
    ? `${ariaLabelPrefix} ${languageTagToLocalName(locale)}`
    : ariaLabelPrefix;

  const handleDropdownButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setDropdownButton(e.currentTarget);

    retracked({
      trackingName: 'language_dropdown',
      action: 'click',
      trackingData: {
        ...trackingInfo,
        locale,
      },
    });
    track('open_navigation_menu', {
      navigationType: 'language',
    });
  };

  return (
    <Element
      css={isSimplifiedPageHeader ? styles.container : styles.entry}
      {...(wrapperElement === 'li' ? { role: 'menuitem' } : {})}
    >
      {useIconButton ? (
        <IconButton
          ref={buttonRef}
          variant="ghost"
          icon={<GlobeIcon size="large" color="support" css={isSimplifiedPageHeader && styles.icon} />}
          aria-label={ariaLabel}
          aria-expanded={isPopoverOpen}
          tooltip={ariaLabel}
          onClick={handleDropdownButtonClick}
        />
      ) : (
        <Button
          ref={buttonRef}
          css={isSimplifiedPageHeader ? styles.preUnifiedButton : styles.button}
          variant="ghost"
          onClick={handleDropdownButtonClick}
          aria-label={ariaLabel}
          aria-expanded={isPopoverOpen}
        >
          <Typography2 css={styles.text} variant="bodyPrimary" component="span">
            <GlobeIcon size="large" color="support" css={isSimplifiedPageHeader && styles.icon} />
            <span {...(isSimplifiedPageHeader ? { css: styles.buttonLabel } : {})}>
              {languageTagToLocalName(locale)}
            </span>
            {isPopoverOpen ? (
              <ChevronUpIcon size="small" css={isSimplifiedPageHeader && styles.chevronIcon} />
            ) : (
              <ChevronDownIcon size="small" css={isSimplifiedPageHeader && styles.chevronIcon} />
            )}
          </Typography2>
        </Button>
      )}
      {isPopoverOpen && (
        <Popover
          anchorElement={dropdownButton}
          open={isPopoverOpen}
          onClose={() => {
            setDropdownButton(null);
          }}
          drawerProps={{ autoFocus: false }}
          dropdownProps={{ marginTop: '0' }}
        >
          <Popover.Body>
            {showTranslationDropdownWithGrouping ? (
              <TranslationDropdownMenuWithGrouping
                languages={languages}
                currentLanguage={locale}
                courseLanguages={courseLanguages}
              />
            ) : (
              <TranslationDropdownMenu languages={languages} currentLanguage={locale} />
            )}
          </Popover.Body>
          <Popover.Footer>
            <Button
              variant="ghost"
              size="medium"
              fullWidth
              onClick={() => {
                retracked({
                  trackingName: 'translation_feedback',
                  action: 'click',
                  trackingData: {
                    ...trackingInfo,
                    locale,
                  },
                });
                setFeedbackDialogOpened(true);
              }}
              icon={<TranslationIcon size="small" />}
              iconPosition="before"
              css={styles.feedbackButton}
            >
              {_t('Add translation feedback')}
            </Button>
          </Popover.Footer>
        </Popover>
      )}
      <TranslationFeedbackDialog
        open={feedbackDialogOpened}
        onClose={() => setFeedbackDialogOpened(false)}
        trackingInfo={trackingInfo}
      />
    </Element>
  );
};

export default TranslationDropdown;
